/**
 * Leading-plus-capped-trailing event coalescer.
 *
 * Realtime bursts: a bulk upload of 200 files emits a `documents` event per
 * row, several times over as each row walks the pipeline. Refetching per event
 * would hammer the database; refetching only at the end would leave the UI
 * frozen for the whole upload.
 *
 * So: run immediately on the first call, then at most once more per window for
 * as long as calls keep arriving. A single upload feels instant, and a burst
 * costs `ceil(duration / windowMs)` refetches instead of one per row.
 *
 * Deliberately not `useDebouncedValue` (`@/hooks/useDebouncedValue`): that is a
 * value hook with a pure *trailing* timer, so a burst arriving faster than the
 * delay pushes the deadline forward on every event and never fires at all.
 */
export interface Coalescer {
  /** Request a run — immediately, or at the end of the open window. */
  call: () => void;
  /** Drop any pending trailing call and close the window. */
  cancel: () => void;
}

export function createCoalescer(fn: () => void, windowMs: number): Coalescer {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pending = false;

  const closeWindow = () => {
    timer = null;
    if (!pending) return;
    pending = false;
    run();
  };

  /**
   * The window is armed BEFORE `fn` runs, never after: a throwing `fn` would
   * otherwise leave no window open at all, so every later event in the burst
   * takes the leading-edge branch and runs immediately — one failed
   * invalidation silently turning coalescing off for the rest of the burst.
   */
  const run = () => {
    timer = setTimeout(closeWindow, windowMs);
    fn();
  };

  const clearWindow = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
    pending = false;
  };

  return {
    call: () => {
      if (timer !== null) {
        pending = true;
        return;
      }
      run();
    },
    cancel: clearWindow,
  };
}
